import {
  AutomationActionDenormalized,
  AutomationActionStatus,
  AutomationActionType
} from '@dadohr/shared/domainTypes/experience';
import { capitalize, compact } from 'lodash';
import { TFunction } from 'i18next';
import { refreshTimestamp } from '../../../../services/firebase';
import { formatDatePrecise } from '../../../../services/time';
import { getSyncDestinationDefinition } from '../../components/Builders/AutomationBuilder/ActionSettings/SyncToDataDestination/helpers';

/*
@guide Create a new automation action - 4.3 Add Info text

This is shown in e.g. Execution Reports to for example say:
- Invite to Google Calendar - [All-Hands Meeting 2023/10/01]
The first part (Invite to...) comes from the action definition we defined in step 2.1,
the part between brackets is defined in this function.
This is where we show some information about the result of this action's execution

Extracted from ParticipantDialog.tsx (previously module-private) so both
ParticipantDialog.tsx and the `issues/` taxonomy module can call it without
a circular import between the two.
*/
export const getAdditionalInfoText = (
  action: AutomationActionDenormalized,
  t: TFunction
) => {
  if (
    action.type === AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION
  ) {
    const eventTitle = `${action.data?.eventName || action.query}`;
    const eventDate = action.data?.eventDate
      ? formatDatePrecise(action?.data.eventDate)
      : null;

    return eventDate ? `${eventTitle} | ${eventDate}` : eventTitle;
  }

  if (action.type === AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP) {
    if (action.data || action.group) {
      return action.group
        ? action.group?.name
        : action.data?.group
        ? action.data?.group?.name
        : '';
    }
    return 'None';
  }

  if (action.type === AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL) {
    return action?.data?.channel?.name || '';
  }

  if (action.type === AutomationActionType.ENROLL_IN_EXPERIENCE) {
    // TODO - Enroll in Experience add links to this
    if (action.data) {
      const tplName = action.data?.tpl?.name || '';
      const enrollDate = refreshTimestamp(
        action.data?.instance?.enrollDate || null
      );
      return `${compact([tplName, formatDatePrecise(enrollDate)]).join(' | ')}`;
    } else {
      return '';
    }
  }

  if (action.type === AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION) {
    const eventTitle = `${action.data?.eventName || action.query}`;
    const eventDate = action.data?.eventDate
      ? formatDatePrecise(action?.data.eventDate)
      : null;

    return eventDate ? `${eventTitle} | ${eventDate}` : eventTitle;
  }

  if (action.type === AutomationActionType.GOOGLE_ADD_TO_GROUP) {
    if (action.data || action.group) {
      return action.group
        ? action.group.name
        : action.data.group
        ? action.data.group.name
        : '';
    }
    return 'None';
  }

  if (action.type === AutomationActionType.OKTA_ADD_TO_GROUP) {
    if (action.data || action.group) {
      return action.group
        ? action.group.name
        : action.data.group
        ? action.data.group.name
        : '';
    }
    return 'None';
  }

  if (action.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE) {
    return compact([
      action.data?.project || action.project?.name,
      action.data?.summary || action.summary
    ]).join(' | ');
  }

  if (action.type === AutomationActionType.ASANA_CREATE_TASK) {
    return `${action.project.name} | ${action.title}`;
  }

  if (action.type === AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT) {
    return `${capitalize(action.content.type)} | ${action.content.name}`;
  }

  if (action.type === AutomationActionType.DOCEBO_ENROLL) {
    return `${action.target.name}`;
  }

  if (action.type === AutomationActionType.SLACK_INVITE_TO_CHANNEL) {
    if (action.data?.channel) {
      return `Channel: ${action.data.channel.name}`;
    }

    if (action.errorData?.data?.channel?.name) {
      return action.errorData?.data?.channel?.name;
    }
    if (action?.channel) {
      return action.channel.type === 'EXISTING'
        ? `Channel: ${action.channel.name}`
        : `Channel: ${action.channel.nameSchema}`;
    }
    return '';
  }

  if (action.type === AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE) {
    return action.status === AutomationActionStatus.DONE
      ? t(`'{{filename}}' created`, {
          filename: action.fileNameFormat
        })
      : t(`Create '{{filename}}'`, {
          filename: action.fileNameFormat
        });
  }

  const truncate = (str: string, n: number) => {
    return str.length > n ? str.slice(0, n - 1) + '...' : str;
  };

  if (action.type === AutomationActionType.HTTP_POST) {
    return action.status === AutomationActionStatus.DONE
      ? t('Made a post request to {{requestUrl}}', {
          requestUrl: action.requestUrl
        })
      : action.status === AutomationActionStatus.ERROR
      ? t('Make a post request to {{requestUrl}}', {
          requestUrl: truncate(action.requestUrl, 120)
        })
      : t('Make a post request to {{requestUrl}}', {
          requestUrl: action.requestUrl
        });
  }

  if (action.type === AutomationActionType.SYNC_TO_DATA_DESTINATION) {
    // Prefer the concrete destination name (e.g. "Salesforce", "Google Sheet")
    // and fall back to the generic phrase when the destination is unset.
    const target = action.destination
      ? getSyncDestinationDefinition(action.destination.type)?.label ??
        t('a data destination')
      : t('a data destination');

    if (action.status === AutomationActionStatus.DONE) {
      return t('Synced participant data to {{target}}.', { target });
    }
    // 'RUNNING' is a synthetic in-flight display status; any
    // other (UPCOMING) is still just scheduled.
    if (action.status === 'RUNNING') {
      return t('Syncing participant data to {{target}}…', { target });
    }
    return t('Sync participant data to {{target}}.', { target });
  }

  // UNENROLL_FROM_EXPERIENCE has no `AutomationActionDenormalized` variant
  // (see AutomationActionDenormalized's union), so this branch is
  // unreachable in practice — TS narrows `action` to `never` here without
  // the cast. Preserved as dead code, matching the original.
  if ((action as { type: AutomationActionType }).type === AutomationActionType.UNENROLL_FROM_EXPERIENCE) {
    return (action as { status: AutomationActionStatus }).status === AutomationActionStatus.DONE
      ? t('Archived this experience')
      : t('Archive this experience');
  }

  return '';
};
